/*
## Summary
Internationalization (i18n) composable. Provides translation and locale utilities to components.

## Responsibilities
- Access current active locale and language settings
- Provide translation function (t) for message keys
- Handle message interpolation with template variables
- Support locale-aware date and time formatting
- Access plural rules and language metadata

## Out of Scope
- Loading new translation files dynamically
- Changing application language at runtime
- Backend translation service integration
- Locale persistence and storage
- Translation key validation

## Dependencies
- Vue I18n library
- Vue 3 composables pattern
- Application translation JSON files
- Locale-specific formatters and rules

## Notes
Wraps Vue I18n instance for composable-based access.
*/
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { supportedLocales, localeStorageKey, isLocale } from '../locales'
import type { Locale } from '@/types/i18n'

export function useAppI18n() {
  const { locale } = useI18n()

  const currentLocale = computed<Locale>(() => (isLocale(locale.value) ? locale.value : 'fr'))

  function setLocale(value: Locale) {
    if (!isLocale(value)) {
      return
    }

    locale.value = value
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(localeStorageKey, value)
    }
  }

  return {
    currentLocale,
    supportedLocales,
    setLocale,
  }
}
